import { ActivatedRoute } from '@angular/router';
import { Component, Input } from '@angular/core';

import { PdfQuotationApiService } from '@data/services/utils/pdf-quotation-api.service';
import { ApiQuoteService } from '../../data/services/api/api-quote.service';
import { ApiQuoteParametersService } from '../../data/services/api/api-quote-parameters.service';
import { MessageService, ConfirmationService } from "primeng/api";
import { IQuote, IAmortizationPeriod } from '@modules/private/quote/data/interfaces/iquote.interface';
import { monthlyRentBreakdown } from '@modules/private/quote/data/utils/monthly-rent.util';

@Component({
  selector: 'app-quote-detail',
  templateUrl: './quote-detail.component.html',
  styleUrls: ['./quote-detail.component.scss'],
  providers: [MessageService, ConfirmationService]
})
export class QuoteDetailComponent {
  @Input() quote?: IQuote; // Para recibir quote desde el componente padre
  
  id!: string;
  quoteParametersId?: string;
  calculatedQuote?: IQuote;
  loading = false;

  // Variable para modal de opciones PDF
  showPdfOptionsDialog = false;

  readonly rentBreakdown = monthlyRentBreakdown;

  constructor(
    private apiQuoteService: ApiQuoteService,
    private apiQuoteParametersService: ApiQuoteParametersService,
    private pdfQuotationService: PdfQuotationApiService,
    private activatedRoute: ActivatedRoute,
    private messageService: MessageService,
  ) {
    // Si viene de una ruta, obtener el ID
    this.id = this.activatedRoute.snapshot.paramMap.get('id') ?? '';
    this.quoteParametersId = this.activatedRoute.snapshot.paramMap.get('parametersId') ?? '';
    
    if (this.id) {
      this.getQuoteData();
    } else if (this.quoteParametersId) {
      this.calculateQuoteFromParameters();
    }
  }

  ngOnInit() {
    // Si se pasa quote como Input, usarlo directamente
    if (this.quote && this.quote._id) {
      this.calculatedQuote = this.quote;
    }
  }

  getQuoteData() {
    this.loading = true;
    this.apiQuoteService.getById(this.id).subscribe({
      next: (response: any) => {
        if (!response.error) {
          this.calculatedQuote = response.data;
        } else {
          console.error(response.msg);
          this.messageService.add({ 
            severity: 'error', 
            summary: 'Error', 
            detail: 'Error al cargar los datos de la cotización' 
          });
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading quote:', error);
        this.messageService.add({ 
          severity: 'error', 
          summary: 'Error', 
          detail: 'Error al cargar los datos de la cotización' 
        });
        this.loading = false;
      }
    });
  }

  calculateQuoteFromParameters() {
    if (!this.quoteParametersId) return;
    
    this.loading = true;
    this.apiQuoteParametersService.calculateQuote(this.quoteParametersId).subscribe({
      next: (response) => {
        if (response.error === false) {
          this.calculatedQuote = response.data;
        } else {
          console.error(response.message);
          this.messageService.add({ 
            severity: 'error', 
            summary: 'Error', 
            detail: response.message || 'Error al calcular la cotización' 
          });
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error calculating quote:', error);
        this.messageService.add({ 
          severity: 'error', 
          summary: 'Error', 
          detail: 'Error al calcular la cotización' 
        });
        this.loading = false;
      }
    });
  }

  downloadPDF() {
    if (!this.calculatedQuote) {
      this.messageService.add({ 
        severity: 'warn', 
        summary: 'Advertencia', 
        detail: 'No hay datos de cotización para generar PDF' 
      });
      return;
    }

    this.showPdfOptionsDialog = true;
  }

  /**
   * Cerrar el diálogo de opciones PDF sin generar nada
   */
  closePdfDialog() {
    this.showPdfOptionsDialog = false;
  }

  /**
   * Generar PDF con la opción de residual seleccionada
   */
  generatePdf(includeResidual: boolean) {
    this.showPdfOptionsDialog = false;
    this.executePdfGeneration(includeResidual);
  }

  private async executePdfGeneration(includeResidual: boolean) {
    this.messageService.add({ 
      severity: 'info', 
      summary: 'Generando PDF', 
      detail: 'Preparando descarga...' 
    });

    try {
      // PDF centralizado en api-rendafin (ADR-0005): Quotation guardada por id, o QuotationParameters por id.
      if (this.id) {
        await this.pdfQuotationService.generateQuotationPdfById(this.id, { includeResidual });
      } else if (this.quoteParametersId) {
        await this.pdfQuotationService.generateQuotationPdf(this.quoteParametersId, { includeResidual });
      }
      this.messageService.add({ 
        severity: 'success', 
        summary: 'Éxito', 
        detail: 'El PDF se ha generado correctamente' 
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      this.messageService.add({ 
        severity: 'error', 
        summary: 'Error', 
        detail: 'Error al generar el PDF' 
      });
    }
  }

  // ===== MÉTODOS PARA MANEJAR NUEVA ESTRUCTURA =====

  /**
   * Obtener información formateada del termData
   */
  getTermDataInfo() {
    if (!this.calculatedQuote?.termData) return null;
    
    const termData = this.calculatedQuote.termData;
    return {
      term: termData.term,
      irr: termData.irr,
      commercialRate: termData.commercialRate,
      totalLeasePayment: termData.totalLeasePayment,
      rentTotal: termData.rent.total,
      residuaryTotal: termData.residuary.total,
      downPaymentTotal: termData.downPayment.total
    };
  }

  /**
   * Obtener tabla de amortización procesada
   */
  getAmortizationPeriods(): IAmortizationPeriod[] {
    if (!this.calculatedQuote?.amortizationPeriods) return [];
    
    // Si es array de strings, necesitaríamos hacer otra llamada al API
    if (typeof this.calculatedQuote.amortizationPeriods[0] === 'string') {
      return [];
    }
    
    return this.calculatedQuote.amortizationPeriods as IAmortizationPeriod[];
  }

  /**
   * Obtener detalles de accesorios formateados
   */
  getAccessoriesDetails() {
    if (!this.calculatedQuote?.termData?.accessoriesDetail) return null;
    
    return {
      cash: this.calculatedQuote.termData.accessoriesDetail.cash,
      financed: this.calculatedQuote.termData.accessoriesDetail.financed
    };
  }

  /**
   * Verificar si hay información de cliente
   */
  hasClientInfo(): boolean {
    return !!(this.calculatedQuote?.client || this.calculatedQuote?.user);
  }

  /**
   * Obtener nombre del cliente o información del usuario
   */
  getClientName(): string {
    if (this.calculatedQuote?.client?.name) {
      return this.calculatedQuote.client.name;
    }
    return 'Cliente no especificado';
  }

  /**
   * Formatear fecha de expiración
   */
  getExpirationDate(): string {
    if (!this.calculatedQuote?.expiresAt) return 'No especificada';
    
    return new Date(this.calculatedQuote.expiresAt).toLocaleDateString('es-ES');
  }

  /**
   * Obtener estado con color
   */
  getStatusSeverity(): string {
    if (!this.calculatedQuote?.status) return 'info';
    
    switch (this.calculatedQuote.status) {
      case 'Activa':
        return 'success';
      case 'Cancelada':
        return 'danger';
      default:
        return 'info';
    }
  }

  // ===== MÉTODOS PARA MANEJAR ACTIVOS DETALLADOS =====

  /**
   * Verificar si hay activos definitivos
   */
  hasDefinitiveAssets(): boolean {
    return !!(this.calculatedQuote?.assets && this.calculatedQuote.assets.length > 0);
  }

  /**
   * Obtener el número total de activos
   */
  getTotalAssetsCount(): number {
    if (this.hasDefinitiveAssets()) {
      return this.calculatedQuote?.assets?.length || 0;
    }
    return this.calculatedQuote?.assetsNames ? 1 : 0;
  }

  /**
   * Obtener lista de activos definitivos con detalles
   */
  getDefinitiveAssets(): any[] {
    if (this.calculatedQuote?.assets && this.calculatedQuote.assets.length > 0) {
      return this.calculatedQuote.assets;
    }
    return [];
  }

  /**
   * Verificar si los activos tienen información detallada
   */
  hasDetailedAssets(): boolean {
    const assets = this.getDefinitiveAssets();
    return assets.length > 0 && assets[0].name && assets[0].category;
  }

  /**
   * Obtener resumen de categorías de activos
   */
  getAssetsCategorySummary(): string {
    const assets = this.getDefinitiveAssets();
    if (assets.length === 0) return '';
    
    const categories = assets.map((asset: any) => asset.category).filter(Boolean);
    const uniqueCategories = [...new Set(categories)];
    
    if (uniqueCategories.length === 1) {
      return uniqueCategories[0] as string;
    } else if (uniqueCategories.length > 1) {
      return `${uniqueCategories.length} categorías diferentes`;
    }
    return '';
  }

  /**
   * Función de tracking para optimizar el renderizado de activos
   */
  trackByAssetId(index: number, asset: any): string {
    return asset._id || index;
  }

  /**
   * Obtener clase CSS para el estado del activo
   */
  getAssetStatusClass(status: string): string {
    switch (status?.toLowerCase()) {
      case 'disponible':
        return 'status-available';
      case 'arrendado':
        return 'status-leased';
      case 'mantenimiento':
        return 'status-maintenance';
      default:
        return 'status-unknown';
    }
  }

  /**
   * Formatear fecha
   */
  formatDate(date: Date | string): string {
    if (!date) return 'No disponible';
    return new Date(date).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Verificar si un valor existe y no está vacío
   */
  hasValue(value: any): boolean {
    return value !== null && value !== undefined && value !== '';
  }
}
